import dayjs, { Dayjs } from 'dayjs'

type RecurrenceConfig = {
  startDate: Dayjs
  endDate: Dayjs | null
  repeatUnits: string
  repeatCount: number
  repeatingDayUnit: string
  repeatPlace: string | null
  repeatMonth: string | null
}

const dayOfWeekMap: Record<string, number> = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
}

const parseRepeatPlace = (repeatPlace: string | null): number[] => {
  if (!repeatPlace) {
    return []
  }

  const placeMap: Record<string, number> = {
    '1st': 1,
    '2nd': 2,
    '3rd': 3,
    '4th': 4,
    '5th': 5,
    'last': -1,
  }

  return repeatPlace
    .split(',')
    .map(p => placeMap[p.trim().toLowerCase()])
    .filter(n => n !== undefined)
}

const parseRepeatMonth = (repeatMonth: string | null): number[] => {
  if (!repeatMonth) {
    return []
  }

  return repeatMonth
    .split(',')
    .map(m => parseInt(m.trim()))
    .filter(n => !isNaN(n) && n >= 1 && n <= 12)
}

const getNthWeekdayOfMonth = (year: number, month: number, targetDay: number, weekNumber: number): Dayjs | null => {
  if (weekNumber === -1) {
    return getLastWeekdayOfMonth(year, month, targetDay)
  }

  const firstOfMonth = dayjs().year(year).month(month - 1).date(1)
  const daysUntilTarget = ((targetDay - firstOfMonth.day()) + 7) % 7
  const firstOccurrence = firstOfMonth.add(daysUntilTarget, 'day')
  const nthOccurrence = firstOccurrence.add(7 * (weekNumber - 1), 'day')

  if (nthOccurrence.month() !== month - 1) {
    return null
  }

  return nthOccurrence
}

const getLastWeekdayOfMonth = (year: number, month: number, targetDay: number): Dayjs => {
  const lastDay = dayjs().year(year).month(month - 1).endOf('month').startOf('day')
  const daysFromTarget = ((lastDay.day() - targetDay) + 7) % 7
  return lastDay.subtract(daysFromTarget, 'day')
}

const generateDailyDates = (startDate: Dayjs, endDate: Dayjs, repeatCount: number): Dayjs[] => {
  const dates: Dayjs[] = []
  const interval = repeatCount || 1
  let current = startDate

  while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
    dates.push(current)
    current = current.add(interval, 'day')
  }

  return dates
}

const generateWeeklyDates = (startDate: Dayjs, endDate: Dayjs, repeatCount: number, targetDay: number): Dayjs[] => {
  const dates: Dayjs[] = []
  const interval = repeatCount || 1
  const daysUntilTarget = ((targetDay - startDate.day()) + 7) % 7
  let current = startDate.add(daysUntilTarget, 'day')

  while (current.isBefore(endDate) || current.isSame(endDate, 'day')) {
    dates.push(current)
    current = current.add(7 * interval, 'day')
  }

  return dates
}

const generateMonthlyDates = (startDate: Dayjs, endDate: Dayjs, repeatCount: number, targetDay: number, repeatPlace: string | null): Dayjs[] => {
  const dates: Dayjs[] = []
  const weekNumbers = parseRepeatPlace(repeatPlace)
  if (weekNumbers.length === 0) {
    weekNumbers.push(1)
  }

  const interval = repeatCount || 1
  let currentMonth = startDate

  while (currentMonth.isBefore(endDate) || currentMonth.isSame(endDate, 'month')) {
    for (const weekNumber of weekNumbers) {
      const date = getNthWeekdayOfMonth(currentMonth.year(), currentMonth.month() + 1, targetDay, weekNumber)

      if (
        date &&
        (date.isAfter(startDate) || date.isSame(startDate, 'day')) &&
        (date.isBefore(endDate) || date.isSame(endDate, 'day')) &&
        date.month() === currentMonth.month()
      ) {
        dates.push(date)
      }
    }

    currentMonth = currentMonth.add(interval, 'month')
  }

  return dates.sort((a, b) => a.valueOf() - b.valueOf())
}

const generateYearlyDates = (startDate: Dayjs, endDate: Dayjs, repeatCount: number, targetDay: number, repeatPlace: string | null, repeatMonth: string | null): Dayjs[] => {
  const dates: Dayjs[] = []
  const weekNumbers = parseRepeatPlace(repeatPlace)
  if (weekNumbers.length === 0) {
    weekNumbers.push(1)
  }

  const monthNumbers = parseRepeatMonth(repeatMonth)
  if (monthNumbers.length === 0) {
    for (let i = 1; i <= 12; i++) {
      monthNumbers.push(i)
    }
  }

  const interval = repeatCount || 1
  let currentYear = startDate.year()

  while (true) {
    for (const monthNumber of monthNumbers) {
      for (const weekNumber of weekNumbers) {
        const date = getNthWeekdayOfMonth(currentYear, monthNumber, targetDay, weekNumber)

        if (
          date &&
          (date.isAfter(startDate) || date.isSame(startDate, 'day')) &&
          (date.isBefore(endDate) || date.isSame(endDate, 'day')) &&
          date.month() === monthNumber - 1
        ) {
          dates.push(date)
        }
      }
    }

    currentYear += interval
    const yearStart = dayjs().year(currentYear).month(0).date(1)
    if (yearStart.isAfter(endDate)) {
      break
    }
  }

  return dates.sort((a, b) => a.valueOf() - b.valueOf())
}

export const expandRecurrenceDates = (config: RecurrenceConfig): Dayjs[] => {
  if (!config.endDate) {
    return []
  }

  const startDate = config.startDate.startOf('day')
  const endDate = config.endDate.startOf('day')

  if (startDate.isAfter(endDate)) {
    return []
  }

  const targetDay = dayOfWeekMap[config.repeatingDayUnit.toLowerCase()] ?? 0

  switch (config.repeatUnits) {
    case 'day':
      return generateDailyDates(startDate, endDate, config.repeatCount)
    case 'week':
      return generateWeeklyDates(startDate, endDate, config.repeatCount, targetDay)
    case 'month':
      return generateMonthlyDates(startDate, endDate, config.repeatCount, targetDay, config.repeatPlace)
    case 'year':
      return generateYearlyDates(startDate, endDate, config.repeatCount, targetDay, config.repeatPlace, config.repeatMonth)
    default:
      return []
  }
}
